import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../shared/services/apiClient";
import { useToast } from "../../../shared/components/SharedUI";
import { getReservaciones, revisarReservacion, cambiarFechaReservacion } from "../services/portalService";

const PAGE_SIZE = 8;

const ESTADO_COLORS = {
  PENDIENTE: { bg: "rgba(245,158,11,0.1)",  border: "rgba(245,158,11,0.3)",  text: "#f59e0b" },
  APROBADA:  { bg: "rgba(74,222,128,0.1)",  border: "rgba(74,222,128,0.3)",  text: "#4ade80" },
  RECHAZADA: { bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.3)", text: "#f87171" },
  CANCELADA: { bg: "rgba(156,163,175,0.1)", border: "rgba(156,163,175,0.3)", text: "#9ca3af" },
};

function Badge({ estado }) {
  const c = ESTADO_COLORS[estado] || ESTADO_COLORS.PENDIENTE;
  return (
    <span style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>
      {estado}
    </span>
  );
}

function Pagination({ page, total, onChange }) {
  const pages = Math.ceil(total / PAGE_SIZE);
  if (pages <= 1) return null;
  const btnStyle = (disabled) => ({
    padding: "6px 12px", borderRadius: 8, border: "1px solid var(--border-card)",
    background: "var(--bg-card)", color: disabled ? "var(--text-secondary)" : "var(--text-primary)",
    cursor: disabled ? "default" : "pointer", fontSize: 13, opacity: disabled ? 0.4 : 1,
  });
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10, marginTop: 16 }}>
      <button style={btnStyle(page === 1)} disabled={page === 1} onClick={() => onChange(page - 1)}>
        <i className="fa-solid fa-chevron-left" />
      </button>
      <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>{page} / {pages}</span>
      <button style={btnStyle(page === pages)} disabled={page === pages} onClick={() => onChange(page + 1)}>
        <i className="fa-solid fa-chevron-right" />
      </button>
    </div>
  );
}

export default function AdminReservaciones() {
  const { t } = useTranslation();
  const { showToast, toastEl } = useToast();

  const [reservaciones, setReservaciones] = useState([]);
  const [filiales,      setFiliales]      = useState([]);
  const [loading, setLoading]             = useState(true);
  const [filtro, setFiltro]               = useState("PENDIENTE");

  // Review modal (step 1)
  const [modal, setModal]   = useState(null);
  const [notas, setNotas]   = useState("");
  const [saving, setSaving] = useState(false);

  // Payment modal (step 2 — approval)
  const [payModal, setPayModal]   = useState(null);
  const [fechaPago, setFechaPago] = useState("");
  const [numDoc, setNumDoc]       = useState("");
  const [payErr, setPayErr]       = useState("");

  // Date change modal
  const [fechaModal, setFechaModal]       = useState(null);
  const [nuevaFecha, setNuevaFecha]       = useState("");
  const [savingFecha, setSavingFecha]     = useState(false);

  // Filters
  const [filterFilial,     setFilterFilial]     = useState("");
  const [filterFechaDesde, setFilterFechaDesde] = useState("");
  const [filterFechaHasta, setFilterFechaHasta] = useState("");
  const [page, setPage] = useState(1);

  function load() {
    getReservaciones().then(d => setReservaciones(d.results || d)).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => {
    load();
    apiFetch("/api/residents/filial/")
      .then(r => r.json())
      .then(d => setFiliales(d.results || d))
      .catch(() => {});
  }, []);

  function resetPage() { setPage(1); }

  // Step 1: open review modal
  function openReview(r) {
    setModal(r);
    setNotas("");
    setPayErr("");
  }

  // Rejection — no payment needed
  async function handleRechazar() {
    setSaving(true);
    try {
      await revisarReservacion(modal.id, { estado: "RECHAZADA", notas_admin: notas });
      showToast(t("portal.reservaRechazada"), false);
      setModal(null); setNotas(""); load();
    } catch { showToast(t("common.errorGuardar"), false); }
    finally { setSaving(false); }
  }

  // Step 2: show payment modal for approval
  function openPayModal() {
    setPayModal(modal);
    setFechaPago("");
    setNumDoc("");
    setPayErr("");
  }

  // Confirm approval with payment data
  async function handleAprobar() {
    if (!fechaPago || !numDoc.trim()) {
      setPayErr(t("portal.pagoRequerido") || "Fecha de pago y número de documento son obligatorios.");
      return;
    }
    setSaving(true); setPayErr("");
    try {
      await revisarReservacion(payModal.id, {
        estado: "APROBADA",
        notas_admin: notas,
        fecha_pago: fechaPago,
        num_doc_pago: numDoc.trim(),
      });
      showToast(t("portal.reservaAprobada"), true);
      setPayModal(null); setModal(null); setNotas(""); load();
    } catch (e) {
      try { const d = JSON.parse(e.message); setPayErr(Object.values(d).flat().join(". ")); }
      catch { setPayErr(e.message || t("common.errorGuardar")); }
    } finally { setSaving(false); }
  }

  // Date change
  async function handleCambiarFecha() {
    if (!nuevaFecha) return;
    setSavingFecha(true);
    try {
      await cambiarFechaReservacion(fechaModal.id, nuevaFecha);
      showToast(t("portal.fechaCambiada") || "Fecha actualizada", true);
      setFechaModal(null); load();
    } catch { showToast(t("common.errorGuardar"), false); }
    finally { setSavingFecha(false); }
  }

  const FILTROS = ["PENDIENTE", "APROBADA", "RECHAZADA", "TODAS"];
  const pendingCount = reservaciones.filter(r => r.estado === "PENDIENTE").length;

  const filtered = reservaciones.filter(r => {
    if (filtro !== "TODAS" && r.estado !== filtro) return false;
    if (filterFilial && r.filial_nombre !== filiales.find(f => String(f.id) === filterFilial)?.idFilial) return false;
    if (filterFechaDesde && r.fecha < filterFechaDesde) return false;
    if (filterFechaHasta && r.fecha > filterFechaHasta) return false;
    return true;
  });
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const inputStyle = {
    padding: "7px 10px", borderRadius: 8, border: "1px solid var(--border-input)",
    background: "var(--hover-bg)", color: "var(--text-primary)", fontSize: 13,
    outline: "none", minWidth: 0,
  };

  const labelStyle = { fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", display: "block", marginBottom: 6, letterSpacing: 0.5 };

  return (
    <div style={{ padding: "24px" }}>
      {toastEl}
      {/* Breadcrumb */}
      <span style={{ color: "var(--text-secondary)", fontWeight: 400 }}>
        <Link to="/" style={{ textDecoration: "none" }}>
          <span style={{ color: "var(--text-secondary)", fontWeight: 400 }}>Portal Admin /</span>
        </Link>
        <span style={{ color: "var(--text-primary)", fontWeight: 600 }}> {t("portal.reservaciones")}</span>
      </span>

      <h2 className="mt-2 me-2 p-0 title">{t("portal.reservaciones")}</h2>
      <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("portal.adminReservDesc")}</p>

      {/* Status filter tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {FILTROS.map(f => (
          <button key={f} onClick={() => { setFiltro(f); resetPage(); }} style={{
            padding: "7px 16px", borderRadius: 20, cursor: "pointer",
            fontWeight: 600, fontSize: 13, border: "none",
            background: filtro === f ? "#f97316" : "var(--hover-bg)",
            color: filtro === f ? "#fff" : "var(--text-secondary)",
            display: "flex", alignItems: "center", gap: 6,
          }}>
            {f === "TODAS" ? t("portal.todas") : f}
            {f === "PENDIENTE" && pendingCount > 0 && (
              <span style={{ background: "#f87171", color: "#fff", borderRadius: 99, padding: "1px 6px", fontSize: 10 }}>
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <select value={filterFilial} onChange={e => { setFilterFilial(e.target.value); resetPage(); }}
          style={{ ...inputStyle, flex: "1 1 140px" }}>
          <option value="">{t("portal.todasLasFiliales")}</option>
          {filiales.map(f => <option key={f.id} value={f.id}>{f.idFilial}</option>)}
        </select>
        <input type="date" value={filterFechaDesde} onChange={e => { setFilterFechaDesde(e.target.value); resetPage(); }}
          title="Desde" style={{ ...inputStyle, flex: "1 1 130px" }} />
        <input type="date" value={filterFechaHasta} onChange={e => { setFilterFechaHasta(e.target.value); resetPage(); }}
          title="Hasta" style={{ ...inputStyle, flex: "1 1 130px" }} />
        {(filterFilial || filterFechaDesde || filterFechaHasta) && (
          <button onClick={() => { setFilterFilial(""); setFilterFechaDesde(""); setFilterFechaHasta(""); setPage(1); }} style={{
            padding: "7px 12px", borderRadius: 8, border: "none",
            background: "rgba(249,115,22,0.1)", color: "#f97316",
            cursor: "pointer", fontSize: 12, fontWeight: 700,
          }}>
            <i className="fa-solid fa-xmark" style={{ marginRight: 4 }} />{t("common.limpiar")}
          </button>
        )}
      </div>

      {loading ? (
        <p style={{ textAlign: "center", color: "var(--text-secondary)" }}>{t("common.cargando")}</p>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", color: "var(--text-secondary)", padding: 48 }}>
          <i className="fa-solid fa-calendar-check" style={{ fontSize: 40, opacity: 0.4, marginBottom: 12, display: "block" }} />
          <p style={{ margin: 0 }}>{t("portal.sinReservaciones")}</p>
        </div>
      ) : (
        <>
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 12, overflow: "hidden" }}>
            {paged.map((r, i) => (
              <div key={r.id} style={{
                padding: "14px 16px",
                borderBottom: i < paged.length - 1 ? "1px solid var(--border-card)" : "none",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: "var(--text-primary)" }}>{r.area_nombre}</p>
                    <p style={{ margin: "2px 0 0", fontSize: 13, color: "var(--text-secondary)" }}>
                      {r.filial_nombre} · {r.horario_nombre} · {r.fecha}
                    </p>
                    {r.usuario_nombre && <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--text-secondary)" }}>{r.usuario_nombre}</p>}
                    {r.factura_numero && (
                      <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-secondary)" }}>
                        <i className="fa-solid fa-file-invoice" style={{ marginRight: 4, color: "#f97316" }} />
                        {r.factura_numero} · {r.recibo_numero && <span>{r.recibo_numero}</span>}
                        {r.fecha_pago && <span style={{ marginLeft: 6 }}>· {r.fecha_pago}</span>}
                      </p>
                    )}
                  </div>
                  <Badge estado={r.estado} />
                </div>
                {r.notas && <p style={{ margin: "4px 0 6px", fontSize: 13, color: "var(--text-secondary)", fontStyle: "italic" }}>{r.notas}</p>}
                {r.notas_admin && (
                  <p style={{ margin: "4px 0 6px", fontSize: 12, color: "var(--text-secondary)" }}>
                    <i className="fa-solid fa-comment" style={{ marginRight: 4 }} />{r.notas_admin}
                  </p>
                )}

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                  {r.comprobante_url && (
                    <a href={r.comprobante_url} target="_blank" rel="noreferrer" style={{
                      background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.3)",
                      color: "#3b82f6", borderRadius: 8, padding: "5px 12px",
                      fontWeight: 600, fontSize: 12, textDecoration: "none",
                    }}>
                      <i className="fa-solid fa-file" style={{ marginRight: 4 }} />
                      {t("portal.verComprobante") || "Ver comprobante"}
                    </a>
                  )}
                  {r.estado === "PENDIENTE" && (
                    <button onClick={() => openReview(r)} style={{
                      background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.3)",
                      color: "#f97316", borderRadius: 8, padding: "5px 14px",
                      cursor: "pointer", fontWeight: 600, fontSize: 13,
                    }}>
                      {t("portal.revisar")}
                    </button>
                  )}
                  {r.estado === "APROBADA" && (
                    <button onClick={() => { setFechaModal(r); setNuevaFecha(r.fecha); }} style={{
                      background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.3)",
                      color: "#818cf8", borderRadius: 8, padding: "5px 12px",
                      cursor: "pointer", fontWeight: 600, fontSize: 12,
                    }}>
                      <i className="fa-solid fa-calendar-pen" style={{ marginRight: 4 }} />
                      {t("portal.cambiarFecha") || "Cambiar fecha"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <Pagination page={page} total={filtered.length} onChange={setPage} />
        </>
      )}

      {/* ── Review modal (step 1) ── */}
      {modal && !payModal && (
        <div onClick={() => setModal(null)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(3px)",
          zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 12, padding: 24, width: "min(480px,94vw)",
            boxShadow: "0 24px 64px var(--shadow)",
          }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: "var(--text-primary)", margin: "0 0 4px" }}>{t("portal.revisarReserva")}</h2>
            <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "0 0 16px" }}>
              {modal.area_nombre} · {modal.filial_nombre} · {modal.fecha} · {modal.horario_nombre}
            </p>

            {modal.comprobante_url && (
              <a href={modal.comprobante_url} target="_blank" rel="noreferrer" style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.25)",
                color: "#3b82f6", borderRadius: 8, padding: "6px 12px",
                fontSize: 13, fontWeight: 600, textDecoration: "none", marginBottom: 14,
              }}>
                <i className="fa-solid fa-file" />
                {t("portal.verComprobante") || "Ver comprobante del residente"}
              </a>
            )}

            <label style={labelStyle}>{t("portal.notasAdmin")} ({t("common.opcional")})</label>
            <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={3}
              placeholder={t("portal.motivoPlaceholder")}
              className="form-control"
              style={{ width: "100%", resize: "vertical", boxSizing: "border-box", marginBottom: 16 }}
            />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <button onClick={handleRechazar} disabled={saving} style={{
                padding: 12, borderRadius: 8, border: "1px solid rgba(248,113,113,0.4)",
                background: "rgba(248,113,113,0.1)", color: "#f87171",
                fontWeight: 700, fontSize: 14, cursor: saving ? "not-allowed" : "pointer",
              }}>
                <i className="fa-solid fa-xmark" style={{ marginRight: 6 }} />{t("portal.rechazar")}
              </button>
              <button onClick={openPayModal} disabled={saving} style={{
                padding: 12, borderRadius: 8, border: "none",
                background: "#f97316", color: "#fff",
                fontWeight: 700, fontSize: 14, cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.7 : 1,
              }}>
                <i className="fa-solid fa-check" style={{ marginRight: 6 }} />{t("portal.aprobar")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Payment modal (step 2) ── */}
      {payModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(3px)",
          zIndex: 1001, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 12, padding: 24, width: "min(440px,94vw)",
            boxShadow: "0 24px 64px var(--shadow)",
          }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: "var(--text-primary)", margin: "0 0 4px" }}>
              {t("portal.datosPago") || "Datos del pago"}
            </h2>
            <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "0 0 18px" }}>
              {payModal.area_nombre} · {payModal.filial_nombre} · {payModal.fecha}
            </p>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>{t("portal.fechaPago") || "Fecha de pago"} *</label>
              <input type="date" value={fechaPago} onChange={e => setFechaPago(e.target.value)} className="form-control" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>{t("portal.numDocPago") || "Número de documento"} *</label>
              <input type="text" value={numDoc} onChange={e => setNumDoc(e.target.value)}
                className="form-control" placeholder="Referencia bancaria / N° comprobante" />
            </div>

            {payErr && (
              <div style={{
                background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)",
                borderLeft: "4px solid #f87171", borderRadius: 8, padding: "10px 14px",
                color: "#f87171", fontSize: 13, marginBottom: 14,
              }}>
                {payErr}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setPayModal(null)} style={{
                padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border-input)",
                background: "var(--hover-bg)", color: "var(--text-secondary)", cursor: "pointer", fontSize: 14,
              }}>
                {t("common.cancelar")}
              </button>
              <button onClick={handleAprobar} disabled={saving} style={{
                padding: "9px 20px", borderRadius: 8, border: "none",
                background: "#22c55e", color: "#fff", fontWeight: 700, fontSize: 14,
                cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1,
              }}>
                <i className="fa-solid fa-check" style={{ marginRight: 6 }} />
                {saving ? t("common.cargando") : (t("portal.confirmarAprobacion") || "Confirmar aprobación")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Date change modal ── */}
      {fechaModal && (
        <div onClick={() => setFechaModal(null)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(3px)",
          zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 12, padding: 24, width: "min(380px,94vw)",
            boxShadow: "0 24px 64px var(--shadow)",
          }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: "var(--text-primary)", margin: "0 0 4px" }}>
              {t("portal.cambiarFecha") || "Cambiar fecha"}
            </h2>
            <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "0 0 16px" }}>
              {fechaModal.area_nombre} · {fechaModal.filial_nombre}
            </p>
            <label style={labelStyle}>{t("portal.nuevaFecha") || "Nueva fecha"}</label>
            <input type="date" value={nuevaFecha} onChange={e => setNuevaFecha(e.target.value)}
              className="form-control" style={{ marginBottom: 16 }} />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setFechaModal(null)} style={{
                padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border-input)",
                background: "var(--hover-bg)", color: "var(--text-secondary)", cursor: "pointer", fontSize: 14,
              }}>
                {t("common.cancelar")}
              </button>
              <button onClick={handleCambiarFecha} disabled={savingFecha} style={{
                padding: "9px 20px", borderRadius: 8, border: "none",
                background: "#f97316", color: "#fff", fontWeight: 700, fontSize: 14,
                cursor: savingFecha ? "not-allowed" : "pointer", opacity: savingFecha ? 0.7 : 1,
              }}>
                {savingFecha ? t("common.cargando") : t("common.guardar")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
